import React from "react";
import * as moment from "moment";
import {
  Form,
  FormGroup,
  TextInput,
  Select,
  SelectItem,
  DatePicker,
  DatePickerInput,
  RadioButton,
  RadioButtonGroup,
  SelectItemGroup,
  Checkbox,
  ComboBox,
} from "carbon-components-react";
import Validations from "web_modules/shared/FormHelpers/validation";
import {
  TaskCategory,
  TaskConfigParamOptions,
  TaskOwner,
  TaskShape,
  TaskStage,
  TASK_CATEGORIES_OPTIONS_BY_OWNER,
  TASK_OWNER_OPTIONS,
  TASK_STAGE_OPTIONS_BY_OWNER,
  TASK_STATUS_OPTIONS,
} from "@eave/shared/types/api/tasks";
import { EditingState } from "web_modules/shared/BaseTable";
import EditDueDateParameters from "./TaskClauseEditors/DueDateParameters";
import {
  dueDateParamsToDisplayString,
  newTaskDefaultIndexOrder,
  taskOrderInitialSelectedItem,
  taskOrderItems,
} from "./helpers";
import TaskClauseEditor from "./TaskClauseEditors/TaskClauseEditor";

const styles = require("./styles.module.scss");

const validateInput = (value, invalidFields, updateInvalidFields, name: string) => {
  if (!value || value.length === 0) {
    updateInvalidFields([...invalidFields, name]);
  } else {
    updateInvalidFields(invalidFields.filter(f => f !== name));
  }
};

interface TaskFormProps {
  taskFormState: any;
  updateTaskFormState: Function;
  invalidFields: string[];
  updateInvalidFields: Function;

  editingMode: EditingState;

  taskOptions: TaskConfigParamOptions;
  tasksList: TaskShape[];

  handlers: any;
  setDisableModalSubmit: Function;
}

const TaskForm: React.FC<TaskFormProps> = ({
  taskFormState,
  updateTaskFormState,
  invalidFields,
  updateInvalidFields,
  editingMode,
  taskOptions,
  tasksList,
  handlers,
  setDisableModalSubmit,
}: TaskFormProps) => {
  const {
    handleTextInputChange,
    handleRadioChange,
    handleSelectChange,
    handleComboBoxChange,
    handleCheckboxChange,
  } = handlers;

  const handleInputChange = event => {
    const { target } = event;
    const { name } = target;

    if (name === "stage") {
      const stage = event.target.value;

      // If the stage is set back to the "Select a stage" item,
      //   allow for the action & show invalid field styling, but do not throw an error
      if (stage === "") {
        updateTaskFormState({ ...taskFormState, stage });
        validateInput(event.target.value, invalidFields, updateInvalidFields, "stage");
        return;
      }

      // Otherwise, process stage changes & re-process index_order defaults
      let changes: any = { stage };

      if (editingMode === EditingState.CREATE) {
        // then also need to update the index order of a new task created
        // based on the existing tasks in that stage
        const index_order = newTaskDefaultIndexOrder(tasksList, stage);
        changes = { ...changes, index_order };
      }
      updateTaskFormState({ ...taskFormState, ...changes });
      validateInput(event.target.value, invalidFields, updateInvalidFields, "stage");
    }
    if (name === "owner") {
      updateTaskFormState({
        ...taskFormState,
        owner: event.target.value,

        // Reset stage and category, as they're dependent on the owner
        stage: "" as TaskStage,
        category: "" as TaskCategory,
        index_order: taskFormState.default_index_order,
      });
      validateInput(event.target.value, invalidFields, updateInvalidFields, "owner");
    }
  };

  const taskStatusRadioGroup = () => (
    <RadioButtonGroup
      valueSelected={taskFormState && taskFormState.status}
      labelPosition="right"
      legend="Task status *"
      name="status"
      id="task-status"
      invalid={invalidFields.includes("status")}
      onChange={status => handleRadioChange(status, "status", [Validations.IsRequired])}
      orientation="horizontal"
    >
      {TASK_STATUS_OPTIONS.map(status => (
        <RadioButton
          id={`radio-${status}`}
          key={`radio-${status}`}
          labelText={status}
          value={status}
        />
      ))}
    </RadioButtonGroup>
  );
  return (
    <Form className={styles.taskForm}>
      <FormGroup legendText="">
        <div style={{ marginBottom: "15px" }} />
        {taskStatusRadioGroup()}
        <div style={{ marginBottom: "25px" }} />
        <TextInput
          id="task-name"
          name="name"
          value={(taskFormState && taskFormState.name) || ""}
          labelText="Task Name *"
          placeholder="Enter a task description"
          type="text"
          invalid={invalidFields.includes("name")}
          invalidText="Enter a valid name"
          onBlur={e => handleTextInputChange(e, "name", [Validations.IsValid])}
          onChange={e => handleTextInputChange(e, "name", [Validations.IsValid])}
          size="sm"
        />
        <div style={{ marginBottom: "10px" }} />
        <Select
          id="task-owner"
          name="owner"
          value={(taskFormState && taskFormState.owner) || ""}
          invalid={invalidFields.includes("owner")}
          invalidText="A valid value is required"
          labelText="Task Owner *"
          style={{ minWidth: "320px" }}
          onBlur={handleInputChange}
          onChange={handleInputChange}
          size="sm"
        >
          <SelectItem text="Select an owner" value="" />
          {TASK_OWNER_OPTIONS.map(owner => (
            <SelectItem key={owner} text={owner} value={owner} />
          ))}
        </Select>
        <div style={{ marginBottom: "10px" }} />
        <Select
          id="task-stage"
          name="stage"
          value={(taskFormState && taskFormState.stage) || ""}
          invalid={invalidFields.includes("stage")}
          invalidText={
            taskFormState && taskFormState.owner ? "Select a stage" : "Select a task owner first"
          }
          labelText="Task Stage *"
          onBlur={handleInputChange}
          onChange={handleInputChange}
          size="sm"
        >
          <SelectItem text="Select a stage" value="" />
          {taskFormState &&
            taskFormState.owner === TaskOwner.Underwriter &&
            TASK_STAGE_OPTIONS_BY_OWNER[TaskOwner.Underwriter].map(stage => (
              <SelectItem key={stage} text={stage} value={stage} />
            ))}
          {taskFormState &&
            taskFormState.owner === TaskOwner.LMLender &&
            TASK_STAGE_OPTIONS_BY_OWNER[TaskOwner.LMLender].map(stage => (
              <SelectItem key={stage} text={stage} value={stage} />
            ))}
        </Select>
        <div style={{ marginBottom: "10px" }} />
        <Select
          id="task-category"
          name="category"
          value={(taskFormState && taskFormState.category) || ""}
          invalid={invalidFields.includes("category")}
          invalidText="A valid value is required"
          labelText="Task Category *"
          style={{ minWidth: "320px" }}
          onBlur={e => handleSelectChange(e, "category", [Validations.IsValid])}
          onChange={e => handleSelectChange(e, "category", [Validations.IsValid])}
          size="sm"
        >
          <SelectItem text="Select a category" value="" />
          {taskFormState && taskFormState.owner === TaskOwner.Underwriter && (
            <SelectItemGroup label={TaskOwner.Underwriter}>
              {TASK_CATEGORIES_OPTIONS_BY_OWNER[TaskOwner.Underwriter].map(cat => (
                <SelectItem key={cat} text={cat} value={cat} />
              ))}
            </SelectItemGroup>
          )}
          {taskFormState && taskFormState.owner === TaskOwner.LMLender && (
            <SelectItemGroup label={TaskOwner.LMLender}>
              {TASK_CATEGORIES_OPTIONS_BY_OWNER[TaskOwner.LMLender].map(cat => (
                <SelectItem key={cat} text={cat} value={cat} />
              ))}
            </SelectItemGroup>
          )}
        </Select>
        <div style={{ marginBottom: "10px" }} />
        <DatePicker
          disabled
          datePickerType="single"
          dateFormat="m/d/Y"
          value={
            (taskFormState && moment(taskFormState.computed_due_date).format("M/D/YYYY")) || ""
          }
        >
          <DatePickerInput
            id="computed-due-date-input"
            disabled
            labelText={`Computed due date (${
              taskFormState &&
              taskFormState.due_date_params &&
              dueDateParamsToDisplayString(taskFormState.due_date_params)
            }`}
          />
        </DatePicker>
        <div style={{ marginBottom: "10px" }} />
        <DatePicker
          datePickerType="single"
          style={{ minWidth: "320px" }}
          dateFormat="m/d/Y"
          value={
            (taskFormState &&
              moment(taskFormState.operator_override_due_date).format("M/D/YYYY")) ||
            ""
          }
          onChange={eventOrDates => {
            let value = eventOrDates.target ? eventOrDates.target.value : eventOrDates[0];
            if (value === "") {
              value = null;
            }
            updateTaskFormState({ ...taskFormState, operator_override_due_date: value });
          }}
        >
          <DatePickerInput
            labelText="Due date override"
            id="date-picker-input"
            onChange={eventOrDates => {
              let value = eventOrDates.target ? eventOrDates.target.value : eventOrDates[0];
              if (value === "") {
                value = null;
              }
              updateTaskFormState({ ...taskFormState, operator_override_due_date: value });
            }}
          />
        </DatePicker>
        <div style={{ marginBottom: "10px" }} />
        {taskFormState && taskFormState.default_index_order !== undefined && (
          <>
            <ComboBox
              titleText="Which task should appear after this one?"
              id="task-order"
              name="order"
              placeholder="(Add to start)" // defaults to top of the list
              light
              items={taskOrderItems(tasksList, taskFormState)}
              itemToString={e => (e ? e.text : "")}
              initialSelectedItem={taskOrderInitialSelectedItem(tasksList, taskFormState)}
              onChange={e => handleComboBoxChange(e, "index_order", taskFormState.index_order)}
            />
            <div style={{ height: "30px" }} />
          </>
        )}
        <div style={{ marginBottom: "10px" }} />
        <Checkbox
          id="task-include-in-queues"
          name="include_in_queues"
          labelText="Include in queues?"
          checked={(taskFormState && taskFormState.include_in_queues) || false}
          onChange={() =>
            handleCheckboxChange(taskFormState.include_in_queues, "include_in_queues")
          }
        />
        <Checkbox
          id="task-is-critical"
          name="is_critical"
          labelText="Is critical?"
          checked={(taskFormState && taskFormState.is_critical) || false}
          onChange={() => handleCheckboxChange(taskFormState.is_critical, "is_critical")}
        />
        <Checkbox
          id="task-pinned"
          name="pinned"
          labelText="Pinned?"
          checked={(taskFormState && taskFormState.pinned) || false}
          onChange={() => handleCheckboxChange(taskFormState.pinned, "pinned")}
        />
        <Checkbox
          id="task-blocks-loan-decision"
          name="blocks_loan_decision"
          labelText="Block Loan Decision?"
          checked={(taskFormState && taskFormState.blocks_loan_decision) || false}
          onChange={() =>
            handleCheckboxChange(taskFormState.blocks_loan_decision, "blocks_loan_decision")
          }
        />
      </FormGroup>
      {taskOptions && (
        <>
          <hr style={{ border: "1px dashed #dadada" }} />
          <FormGroup legendText="Due Date Parameters">
            <EditDueDateParameters
              options={taskOptions}
              formState={taskFormState && taskFormState.due_date_params}
              setFormState={due_date_params => {
                updateTaskFormState({ ...taskFormState, due_date_params });
              }}
            />
          </FormGroup>

          <hr style={{ border: "1px dashed #dadada" }} />
          <FormGroup legendText="Completion Conditions">
            <TaskClauseEditor
              typeOfClause="complete"
              taskOptions={taskOptions}
              formState={taskFormState && taskFormState.completion_conditions}
              setFormState={completion_conditions => {
                // If user resets completion_conditions, delete the key
                if (
                  completion_conditions.operator === "" &&
                  taskFormState.completion_conditions !== undefined
                ) {
                  updateTaskFormState({
                    ...taskFormState,
                    completion_conditions: null,
                  });
                  return;
                }
                updateTaskFormState({ ...taskFormState, completion_conditions });
              }}
              setDisableModalSubmit={setDisableModalSubmit}
            />
          </FormGroup>
        </>
      )}
    </Form>
  );
};

export default TaskForm;
